// SkyDome.js — Satisfying, high-res cinematic dome (three r150+)
// API: setupBackground(scene, camera) / disposeSkyDome(scene)
//      applyRandomPreset() / applySkyPreset(name)
//      transitionToPreset(name, duration) / transitionToLightMode(duration) / transitionToDarkMode(duration)
// Notes:
//  - Camera must see layer 31 (camera.layers.enable(31)).
//  - Pure fragment shader (GPU). No CPU except time uniform.
//  - Looks best with ACES tone mapping + sRGB output.

import * as THREE from "three";

let _sky = null;
let _raf = null;
let _last = 0;
let _transitionRaf = null;
let _currentMode = 'dark';
let _lastDarkPreset = null;

export function setupBackground(scene, camera) {
  scene.background = null;
  scene.environment = null;
  scene.fog = null;

  disposeSkyDome(scene);
  _sky = buildSky(scene, camera);

  const chosen = applyRandomPreset();
  // console.log("[SkyDome] preset:", chosen);

  startRAF();
  return _sky;
}

export function disposeSkyDome(scene) {
  stopRAF();
  if (!_sky) return;
  scene.remove(_sky);
  _sky.geometry?.dispose?.();
  _sky.material?.dispose?.();
  _sky = null;
}

export function applyRandomPreset() {
  if (!_sky) return null;
  const keys = Object.keys(PRESETS);
  const name = keys[(Math.random() * keys.length) | 0];
  applySkyPreset(name);
  return name;
}

export function applySkyPreset(name) {
  if (!_sky) return;
  const p = PRESETS[name];
  if (!p) return;
  const u = _sky.material.uniforms;

  // palette
  u.uZenithColor.value.set(p.zenith);
  u.uHorizonColor.value.set(p.horizon);
  u.uAuroraA.value.set(p.auroraA);
  u.uAuroraB.value.set(p.auroraB);
  u.uNebulaA.value.set(p.nebulaA || p.auroraA);
  u.uNebulaB.value.set(p.nebulaB || p.auroraB);

  // strengths / shaping
  u.uGlowHeight.value     = p.glowHeight ?? 0.60;
  u.uGlowStrength.value   = p.glowStrength ?? 0.14;
  u.uWispStrength.value   = p.wisps ?? 0.55;
  u.uNebulaStrength.value = p.nebula ?? 0.70;
  u.uVignette.value       = p.vignette ?? 0.22;

  // stars
  u.uStarDensity.value    = p.stars ?? 0.006;
  u.uStarSize.value       = p.starSize ?? 1.0;
  u.uStarTwinkle.value    = p.twinkle ?? 0.9;

  // halo band
  u.uHaloStrength.value   = p.halo ?? 0.26;
  u.uHaloWidth.value      = p.haloWidth ?? 0.42;
  u.uHaloOffset.value     = p.haloOffset ?? 0.0;

  // motion / detail
  u.uAnimSpeed.value      = p.speed ?? 1.0;
  u.uCycleSpeed.value     = p.cycle ?? 0.04;
  u.uNoiseScale.value     = p.noiseScale ?? 1.0;

  // post aid
  u.uDitherAmp.value      = p.dither ?? (1.0/255.0);
}

// ---------- internal RAF ----------
function startRAF() {
  stopRAF();
  _last = performance.now();
  const loop = (t) => {
    if (!_sky) return;
    const dt = Math.min(0.05, (t - _last) / 1000);
    _last = t;
    const u = _sky.material.uniforms;
    u.uTime.value  += dt * u.uAnimSpeed.value;
    u.uCycle.value += dt * u.uCycleSpeed.value;
    _raf = requestAnimationFrame(loop);
  };
  _raf = requestAnimationFrame(loop);
}
function stopRAF(){ if (_raf) cancelAnimationFrame(_raf); _raf = null; }

// ---------- build high-res dome ----------
function buildSky(scene, camera) {
  // Higher segments for buttery gradients along silhouette
  const geo = new THREE.SphereGeometry(1, 128, 96);
  geo.deleteAttribute("normal");

  const vert = /* glsl */`
    uniform float uSkyRadius;
    varying vec3 vDir;
    void main(){
      vec3 pos = normalize(position) * uSkyRadius;
      vDir = normalize(pos);
      gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
    }
  `;

  const frag = /* glsl */`
    precision highp float;
    varying vec3 vDir;

    uniform float uTime, uCycle;
    uniform vec3  uZenithColor, uHorizonColor;
    uniform vec3  uAuroraA, uAuroraB;
    uniform vec3  uNebulaA, uNebulaB;

    uniform float uGlowHeight, uGlowStrength;
    uniform float uWispStrength, uNebulaStrength;
    uniform float uVignette;

    uniform float uHaloStrength, uHaloWidth, uHaloOffset;

    uniform float uStarDensity, uStarSize, uStarTwinkle;

    uniform float uAnimSpeed, uCycleSpeed, uNoiseScale;
    uniform float uDitherAmp;

    // --- hash / noise / fbm ---
    float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7))) * 43758.5453123); }

    float noise(vec2 p){
      vec2 i = floor(p), f = fract(p);
      float a = hash(i);
      float b = hash(i+vec2(1.0,0.0));
      float c = hash(i+vec2(0.0,1.0));
      float d = hash(i+vec2(1.0,1.0));
      vec2 u = f*f*(3.0 - 2.0*f);
      return mix(a,b,u.x) + (c-a)*u.y*(1.0-u.x) + (d-b)*u.x*u.y;
    }

    float fbm(vec2 p){
      float v=0.0; float a=0.5;
      mat2 m = mat2(1.6,-1.2,1.2,1.6);
      for (int i=0;i<6;i++){      // more octaves for richer detail
        v += a * noise(p);
        p = m * p;
        a *= 0.5;
      }
      return v;
    }

    // central differences for gradient
    vec2 grad(vec2 p){
      float e = 0.0015;
      float nx1 = noise(p+vec2(e,0.0));
      float nx2 = noise(p-vec2(e,0.0));
      float ny1 = noise(p+vec2(0.0,e));
      float ny2 = noise(p-vec2(0.0,e));
      return vec2((nx1-nx2)/(2.0*e), (ny1-ny2)/(2.0*e));
    }

    // curl-like flow from noise gradient (swirly, satisfying)
    vec2 curl(vec2 p){
      vec2 g = grad(p);
      vec2 c = vec2(g.y, -g.x);
      return c;
    }

    vec2 flow(vec2 p, float t){
      // 2 layered curl fields with slight time drift
      vec2 f1 = curl(p*1.1 + vec2( 0.05*t, 0.00));
      vec2 f2 = curl(p*2.3 + vec2(-0.03*t, 0.07*t) + 10.0);
      return p + 0.35*f1 + 0.25*f2;
    }

    // dither
    float dither(vec2 uv){
      float g = fract(uv.x*0.754877666 + uv.y*0.569840296 + 0.5);
      return g - 0.5;
    }

    void main(){
      // spherical coords
      float v   = clamp(vDir.y*0.5+0.5, 0.0, 1.0);  // 0 horizon..1 zenith
      float yy  = clamp(vDir.y, -1.0, 1.0);
      float phi = atan(vDir.z, vDir.x);             // -PI..PI

      // base gradient
      vec3 sky = mix(uHorizonColor, uZenithColor, pow(v, 1.6));

      // horizon glow (warm lift near horizon)
      float glow = smoothstep(0.0, uGlowHeight, 1.0 - v) * uGlowStrength;
      sky += glow * vec3(1.0, 0.95, 0.90);

      // mapping space
      float t = uTime;
      vec2  uv0 = vec2(phi*0.5, yy*0.9) * uNoiseScale;

      // advected “curl flow” for satisfying, fluid motion
      vec2 adv = uv0;
      adv = flow(adv, t);
      adv = flow(adv, t*0.7 + 6.0);

      // soft NEBULA fog (color drifts over time)
      float nA = fbm(adv*1.3 + vec2(0.00, 0.08*t));
      float nB = fbm(adv*2.4 + vec2(0.06*t, -0.03*t));
      float neb = smoothstep(0.45, 0.92, 0.5*nA + 0.5*nB);
      float hue = 0.5 + 0.5*sin(uCycle*0.5 + nB*6.2831);
      vec3  nebCol = mix(uNebulaA, uNebulaB, hue);
      float nebMask = smoothstep(0.18, 0.95, v);  // prefer upper mid-sky
      sky = mix(sky, sky + nebCol * neb * nebMask, uNebulaStrength);

      // aurora curtains (anisotropic streaks, gently drifting)
      vec2 auv = vec2(phi*0.42, yy*1.7);
      auv += 0.03*vec2(t, 0.0) + 0.07*vec2(0.0, sin(t*0.3));
      float streak = fbm(vec2(auv.x*2.2, auv.y*0.25));
      streak = pow(streak, 3.5);
      streak *= smoothstep(0.15, 0.98, v);
      vec3 aurCol = mix(uAuroraA, uAuroraB, 0.5 + 0.5*sin(uCycle*0.35 + nA*12.0));
      sky += aurCol * streak * uWispStrength;

      // subtle “breathing” ripple that feels satisfying but not distracting
      float ripple = 0.015 * sin(uv0.x*6.0 + t*0.8) * smoothstep(0.25, 0.95, v);
      sky += ripple * aurCol;

      // stars: two tiers (rare glint + regular pinpricks)
      vec2 grid = floor(vec2(phi/6.2831853 + 0.5, v) * 1200.0);
      float h   = hash(grid);
      float starMask = step(1.0 - uStarDensity*250.0, h);
      if (starMask > 0.0){
        float tw = 0.5 + 0.5*sin(t*3.0 + h*1200.0);
        float size = mix(0.7, 1.8, fract(h*97.0)) * uStarSize;
        vec3  starCol = mix(vec3(0.92,0.94,1.0), vec3(1.0,0.96,0.92), fract(h*19.0));
        float sparkle = pow(tw, 3.0) * uStarTwinkle;
        sky += starCol * sparkle * size * 0.0075;
      }
      // rare bigger glints
      float glint = step(0.9975, hash(grid+21.7));
      if (glint > 0.0){
        float gt = 0.5 + 0.5*sin(t*5.0 + hash(grid+9.3)*40.0);
        sky += vec3(1.0,0.98,0.92) * gt * 0.015;
      }

      // cinematic HALO band (sweeps horizon)
      float haloAngle = phi + uHaloOffset + 0.15*sin(uCycle*0.2);
      float band = 1.0 - smoothstep(0.0, uHaloWidth, abs(haloAngle));
      float nearH = smoothstep(0.0, 0.18, 1.0 - v);
      sky += band * nearH * uHaloStrength * vec3(1.0, 0.9, 0.7);

      // vignette to frame the center (where the model lives)
      float vig = pow(v, 0.35);
      sky *= mix(1.0, 1.0 - uVignette, 1.0 - vig);

      // tiny dither to defeat banding in smooth areas
      sky += uDitherAmp * dither(gl_FragCoord.xy);

      gl_FragColor = vec4(sky, 1.0);
      #include <tonemapping_fragment>
      #include <colorspace_fragment>
    }
  `;

  const mat = new THREE.ShaderMaterial({
    vertexShader: vert,
    fragmentShader: frag,
    side: THREE.BackSide,
    depthWrite: false,
    depthTest: false,
    fog: false,
    toneMapped: true,
    uniforms: {
      uTime:         { value: 0 },
      uCycle:        { value: 0 },
      uSkyRadius:    { value: 5000 },

      uZenithColor:  { value: new THREE.Color("#121a2b") },
      uHorizonColor: { value: new THREE.Color("#2a2340") },
      uAuroraA:      { value: new THREE.Color("#64ffd2") },
      uAuroraB:      { value: new THREE.Color("#6c5bd8") },
      uNebulaA:      { value: new THREE.Color("#38a6ff") },
      uNebulaB:      { value: new THREE.Color("#b28cff") },

      uGlowHeight:   { value: 0.60 },
      uGlowStrength: { value: 0.14 },
      uWispStrength: { value: 0.55 },
      uNebulaStrength:{ value: 0.70 },
      uVignette:     { value: 0.22 },

      uHaloStrength: { value: 0.26 },
      uHaloWidth:    { value: 0.42 },
      uHaloOffset:   { value: 0.0 },

      uStarDensity:  { value: 0.006 },
      uStarSize:     { value: 1.0 },
      uStarTwinkle:  { value: 0.9 },

      uAnimSpeed:    { value: 1.0 },
      uCycleSpeed:   { value: 0.04 },
      uNoiseScale:   { value: 1.0 },

      uDitherAmp:    { value: 1.0/255.0 },
    },
  });

  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = "SkyDome";
  mesh.renderOrder = -9999;
  mesh.frustumCulled = false;
  mesh.matrixAutoUpdate = false;
  mesh.layers.set(31);

  // pin to camera + keep radius just inside far plane
  mesh.userData._lastRadius = -1;
  mesh.onBeforeRender = () => {
    const R = Math.max(50.0, camera.far * 0.985);
    if (R !== mesh.userData._lastRadius) {
      mat.uniforms.uSkyRadius.value = R;
      mesh.userData._lastRadius = R;
    }
    mesh.position.copy(camera.position);
    mesh.updateMatrix();
  };

  scene.add(mesh);
  return mesh;
}

// ---------- presets: saturated but tasteful ----------
const PRESETS = {
  AuroraRoyale: {
    zenith:"#0c1226", horizon:"#1b1440",
    auroraA:"#64ffd2", auroraB:"#6c5bd8",
    nebulaA:"#38a6ff", nebulaB:"#b28cff",
    glowHeight:0.58, glowStrength:0.13,
    wisps:0.58, nebula:0.70, vignette:0.24,
    stars:0.006, starSize:1.1, twinkle:0.9,
    halo:0.24, haloWidth:0.44, haloOffset:0.0,
    speed:1.0, cycle:0.04, noiseScale:1.0, dither:1/255,
  },
  NeonNocturne: {
    zenith:"#081219", horizon:"#1a2137",
    auroraA:"#00ffd1", auroraB:"#00a0ff",
    nebulaA:"#8c3bff", nebulaB:"#20ffe8",
    glowHeight:0.50, glowStrength:0.11,
    wisps:0.50, nebula:0.75, vignette:0.26,
    stars:0.004, starSize:1.2, twinkle:0.85,
    halo:0.30, haloWidth:0.36, haloOffset:0.6,
    speed:1.2, cycle:0.06, noiseScale:1.1, dither:1/255,
  },
  GoldenHourBloom: {
    zenith:"#17202a", horizon:"#403019",
    auroraA:"#ffb36b", auroraB:"#ffe27a",
    nebulaA:"#ff7a4f", nebulaB:"#ffd06a",
    glowHeight:0.72, glowStrength:0.18,
    wisps:0.32, nebula:0.58, vignette:0.18,
    stars:0.002, starSize:1.0, twinkle:0.6,
    halo:0.38, haloWidth:0.66, haloOffset:-0.4,
    speed:0.8, cycle:0.03, noiseScale:0.9, dither:1/255,
  },
  DeepSpaceOpal: {
    zenith:"#060a12", horizon:"#121a2c",
    auroraA:"#8fe2ff", auroraB:"#ff9bf8",
    nebulaA:"#57b6ff", nebulaB:"#caa6ff",
    glowHeight:0.48, glowStrength:0.12,
    wisps:0.54, nebula:0.78, vignette:0.25,
    stars:0.010, starSize:1.0, twinkle:1.0,
    halo:0.14, haloWidth:0.30, haloOffset:0.25,
    speed:1.1, cycle:0.05, noiseScale:1.2, dither:1/255,
  },
  DuskVelvet: {
    zenith:"#0f1731", horizon:"#3a284a",
    auroraA:"#b06cff", auroraB:"#5ec8ff",
    nebulaA:"#4d7aff", nebulaB:"#c399ff",
    glowHeight:0.62, glowStrength:0.13,
    wisps:0.46, nebula:0.62, vignette:0.22,
    stars:0.004, starSize:1.0, twinkle:0.75,
    halo:0.22, haloWidth:0.42, haloOffset:-0.2,
    speed:0.95, cycle:0.04, noiseScale:1.0, dither:1/255,
  },
  DayLight: {
    zenith:"#87CEEB", horizon:"#EAF4FA",
    auroraA:"#ffffff", auroraB:"#e8f4fc",
    nebulaA:"#d4e8f2", nebulaB:"#f0f8ff",
    glowHeight:0.85, glowStrength:0.25,
    wisps:0.08, nebula:0.12, vignette:0.08,
    stars:0.0, starSize:0.0, twinkle:0.0,
    halo:0.35, haloWidth:0.90, haloOffset:0.0,
    speed:0.3, cycle:0.01, noiseScale:0.6, dither:1/255,
  },
};

// ---------- Smooth Transition System ----------
function getCurrentUniforms() {
  if (!_sky) return null;
  const u = _sky.material.uniforms;
  return {
    zenith: u.uZenithColor.value.clone(),
    horizon: u.uHorizonColor.value.clone(),
    auroraA: u.uAuroraA.value.clone(),
    auroraB: u.uAuroraB.value.clone(),
    nebulaA: u.uNebulaA.value.clone(),
    nebulaB: u.uNebulaB.value.clone(),
    glowHeight: u.uGlowHeight.value,
    glowStrength: u.uGlowStrength.value,
    wisps: u.uWispStrength.value,
    nebula: u.uNebulaStrength.value,
    vignette: u.uVignette.value,
    stars: u.uStarDensity.value,
    starSize: u.uStarSize.value,
    twinkle: u.uStarTwinkle.value,
    halo: u.uHaloStrength.value,
    haloWidth: u.uHaloWidth.value,
    haloOffset: u.uHaloOffset.value,
    speed: u.uAnimSpeed.value,
    cycle: u.uCycleSpeed.value,
    noiseScale: u.uNoiseScale.value,
  };
}

function getPresetColors(p) {
  return {
    zenith: new THREE.Color(p.zenith),
    horizon: new THREE.Color(p.horizon),
    auroraA: new THREE.Color(p.auroraA),
    auroraB: new THREE.Color(p.auroraB),
    nebulaA: new THREE.Color(p.nebulaA || p.auroraA),
    nebulaB: new THREE.Color(p.nebulaB || p.auroraB),
    glowHeight: p.glowHeight ?? 0.60,
    glowStrength: p.glowStrength ?? 0.14,
    wisps: p.wisps ?? 0.55,
    nebula: p.nebula ?? 0.70,
    vignette: p.vignette ?? 0.22,
    stars: p.stars ?? 0.006,
    starSize: p.starSize ?? 1.0,
    twinkle: p.twinkle ?? 0.9,
    halo: p.halo ?? 0.26,
    haloWidth: p.haloWidth ?? 0.42,
    haloOffset: p.haloOffset ?? 0.0,
    speed: p.speed ?? 1.0,
    cycle: p.cycle ?? 0.04,
    noiseScale: p.noiseScale ?? 1.0,
  };
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function lerpColor(a, b, t) {
  const result = a.clone();
  result.lerp(b, t);
  return result;
}

function lerpValue(a, b, t) {
  return a + (b - a) * t;
}

export function transitionToPreset(name, duration = 2.0) {
  if (!_sky) return Promise.resolve();
  const p = PRESETS[name];
  if (!p) return Promise.resolve();

  if (_transitionRaf) {
    cancelAnimationFrame(_transitionRaf);
    _transitionRaf = null;
  }

  const startState = getCurrentUniforms();
  const endState = getPresetColors(p);
  const startTime = performance.now();
  const durationMs = duration * 1000;

  return new Promise((resolve) => {
    const animate = (now) => {
      const elapsed = now - startTime;
      const rawProgress = Math.min(elapsed / durationMs, 1.0);
      const progress = easeInOutCubic(rawProgress);

      const u = _sky.material.uniforms;

      u.uZenithColor.value.copy(lerpColor(startState.zenith, endState.zenith, progress));
      u.uHorizonColor.value.copy(lerpColor(startState.horizon, endState.horizon, progress));
      u.uAuroraA.value.copy(lerpColor(startState.auroraA, endState.auroraA, progress));
      u.uAuroraB.value.copy(lerpColor(startState.auroraB, endState.auroraB, progress));
      u.uNebulaA.value.copy(lerpColor(startState.nebulaA, endState.nebulaA, progress));
      u.uNebulaB.value.copy(lerpColor(startState.nebulaB, endState.nebulaB, progress));

      u.uGlowHeight.value = lerpValue(startState.glowHeight, endState.glowHeight, progress);
      u.uGlowStrength.value = lerpValue(startState.glowStrength, endState.glowStrength, progress);
      u.uWispStrength.value = lerpValue(startState.wisps, endState.wisps, progress);
      u.uNebulaStrength.value = lerpValue(startState.nebula, endState.nebula, progress);
      u.uVignette.value = lerpValue(startState.vignette, endState.vignette, progress);

      u.uStarDensity.value = lerpValue(startState.stars, endState.stars, progress);
      u.uStarSize.value = lerpValue(startState.starSize, endState.starSize, progress);
      u.uStarTwinkle.value = lerpValue(startState.twinkle, endState.twinkle, progress);

      u.uHaloStrength.value = lerpValue(startState.halo, endState.halo, progress);
      u.uHaloWidth.value = lerpValue(startState.haloWidth, endState.haloWidth, progress);
      u.uHaloOffset.value = lerpValue(startState.haloOffset, endState.haloOffset, progress);

      u.uAnimSpeed.value = lerpValue(startState.speed, endState.speed, progress);
      u.uCycleSpeed.value = lerpValue(startState.cycle, endState.cycle, progress);
      u.uNoiseScale.value = lerpValue(startState.noiseScale, endState.noiseScale, progress);

      if (rawProgress < 1.0) {
        _transitionRaf = requestAnimationFrame(animate);
      } else {
        _transitionRaf = null;
        resolve();
      }
    };
    _transitionRaf = requestAnimationFrame(animate);
  });
}

export function transitionToLightMode(duration = 2.5) {
  if (_currentMode === 'light') return Promise.resolve();
  
  if (_currentMode === 'dark') {
    _lastDarkPreset = getCurrentUniforms();
  }
  
  _currentMode = 'light';
  return transitionToPreset('DayLight', duration);
}

export function transitionToDarkMode(duration = 2.5) {
  if (_currentMode === 'dark') return Promise.resolve();
  
  _currentMode = 'dark';
  
  if (_lastDarkPreset) {
    return transitionToSavedState(_lastDarkPreset, duration);
  } else {
    const darkPresets = Object.keys(PRESETS).filter(k => k !== 'DayLight');
    const randomDark = darkPresets[Math.floor(Math.random() * darkPresets.length)];
    return transitionToPreset(randomDark, duration);
  }
}

function transitionToSavedState(savedState, duration = 2.5) {
  if (!_sky) return Promise.resolve();

  if (_transitionRaf) {
    cancelAnimationFrame(_transitionRaf);
    _transitionRaf = null;
  }

  const startState = getCurrentUniforms();
  const endState = savedState;
  const startTime = performance.now();
  const durationMs = duration * 1000;

  return new Promise((resolve) => {
    const animate = (now) => {
      const elapsed = now - startTime;
      const rawProgress = Math.min(elapsed / durationMs, 1.0);
      const progress = easeInOutCubic(rawProgress);

      const u = _sky.material.uniforms;

      u.uZenithColor.value.copy(lerpColor(startState.zenith, endState.zenith, progress));
      u.uHorizonColor.value.copy(lerpColor(startState.horizon, endState.horizon, progress));
      u.uAuroraA.value.copy(lerpColor(startState.auroraA, endState.auroraA, progress));
      u.uAuroraB.value.copy(lerpColor(startState.auroraB, endState.auroraB, progress));
      u.uNebulaA.value.copy(lerpColor(startState.nebulaA, endState.nebulaA, progress));
      u.uNebulaB.value.copy(lerpColor(startState.nebulaB, endState.nebulaB, progress));

      u.uGlowHeight.value = lerpValue(startState.glowHeight, endState.glowHeight, progress);
      u.uGlowStrength.value = lerpValue(startState.glowStrength, endState.glowStrength, progress);
      u.uWispStrength.value = lerpValue(startState.wisps, endState.wisps, progress);
      u.uNebulaStrength.value = lerpValue(startState.nebula, endState.nebula, progress);
      u.uVignette.value = lerpValue(startState.vignette, endState.vignette, progress);

      u.uStarDensity.value = lerpValue(startState.stars, endState.stars, progress);
      u.uStarSize.value = lerpValue(startState.starSize, endState.starSize, progress);
      u.uStarTwinkle.value = lerpValue(startState.twinkle, endState.twinkle, progress);

      u.uHaloStrength.value = lerpValue(startState.halo, endState.halo, progress);
      u.uHaloWidth.value = lerpValue(startState.haloWidth, endState.haloWidth, progress);
      u.uHaloOffset.value = lerpValue(startState.haloOffset, endState.haloOffset, progress);

      u.uAnimSpeed.value = lerpValue(startState.speed, endState.speed, progress);
      u.uCycleSpeed.value = lerpValue(startState.cycle, endState.cycle, progress);
      u.uNoiseScale.value = lerpValue(startState.noiseScale, endState.noiseScale, progress);

      if (rawProgress < 1.0) {
        _transitionRaf = requestAnimationFrame(animate);
      } else {
        _transitionRaf = null;
        resolve();
      }
    };
    _transitionRaf = requestAnimationFrame(animate);
  });
}

export function getCurrentMode() {
  return _currentMode;
}

export function setCurrentMode(mode) {
  _currentMode = mode;
}
